<template>
  <div class="idents-table">
    <!-- FILTERS START -->
    <v-card-title class="filters grey lighten-5">
      <div class="filters__header">
        <v-tooltip
          v-if="!hasFilters"
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="secondary"
              v-on="on"
            >
              <v-icon>
                filter_list
              </v-icon>
            </v-btn>
          </template>
          <span>Filters</span>
        </v-tooltip>
        <v-tooltip
          v-else
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="primary"
              v-on="on"
              @click="resetFilters"
            >
              <v-icon>
                restore
              </v-icon>
            </v-btn>
          </template>
          <span>reset filters</span>
        </v-tooltip>
      </div>
      <v-select
        v-model="selectedProviders"
        :items="identProviders"
        label="Provider"
        item-text="name"
        item-value="name"
        item-key="id"
        multiple
        @input="onFilterChange"
      />
      <v-select
        v-model="selectedIdentStatus"
        :items="identStatuses"
        label="Ident status"
        item-text="title"
        item-value="value"
        multiple
        @input="onFilterChange"
      />
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Filter ident results"
        single-line
      />
    </v-card-title>
    <!-- FILTERS END -->

    <v-divider />
    <!-- IDENTS DATA TABLE START -->

    <v-data-table
      :headers="headers"
      :items="idents"
      :total-items="totalIdents"
      :pagination.sync="pagination"
      :loading="isLoading"
      :no-data-text="tableText"
      loading-text="Loading..."
      disable-initial-sort
      :expand="false"
    >
      <!-- IDENTS DATA TABLE ROW START -->
      <template
        slot="items"
        slot-scope="props"
      >
        <tr :class="{'idents-table-row__expanded': props.expanded}">
          <td>{{ props.item.id }}</td>
          <td class="text-xs">
            {{ props.item.userId }}
          </td>
          <td class="text-xs">
            {{ props.item.provider }}
          </td>
          <td class="text-xs">
            <v-chip
              :color="props.item.state === 'success' ? 'success' : 'secondary lighten-1'"
              outline
              small
            >
              {{ props.item.state }}
            </v-chip>
          </td>
          <td class="text-xs">
            <v-chip
              v-if="props.item.state === 'success'"
              outline
              small
              :color="props.item.hasValidDocs === true ? 'success' : 'orange lighten-1'"
            >
              {{ (props.item.hasValidDocs === true ? 'valid' : 'expired') }}
            </v-chip>
          </td>
          <td class="text-xs">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  flat
                  small
                  color="primary"
                  style="margin: 0 -8px; text-transform: lowercase;"
                  @click.stop="getUserIdentificationFiles({
                    id:props.item.userId, identType:props.item.provider
                  })"
                  v-on="on"
                >
                  {{ getUserFolder(props.item.userId, props.item.provider) }}
                </v-btn>
              </template>
              <span>Download user identification files</span>
            </v-tooltip>
          </td>
          <td class="text-xs">
            {{ props.item.createdAt }}
          </td>
          <td class="text-xs">
            {{ props.item.updatedAt }}
          </td>
          <td class="text-xs">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="primary"
                  flat
                  icon
                  v-on="on"
                  @click.stop="props.expanded = !props.expanded"
                >
                  <v-icon>{{ props.expanded ? 'visibility_off' : 'visibility' }}</v-icon>
                </v-btn>
              </template>
              <span>{{ props.expanded ? 'Close': 'Show' }} ident detail</span>
            </v-tooltip>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  :disabled="!props.item.hasValidDocs"
                  color="error"
                  flat
                  icon
                  @click.stop="openApproveIdentExpireDialog(props.item.userId)"
                  v-on="on"
                >
                  <v-icon>event_busy</v-icon>
                </v-btn>
              </template>
              <span>Expire result</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
      <!-- IDENTS DATA TABLE ROW END -->

      <!-- IDENT DETAILS EXPANDABLE CONTENT -->
      <template v-slot:expand="props">
        <IdentDetailCard
          :ident="props.item"
          :get-user-identification-files="getUserIdentificationFiles"
        />
      </template>
      <template
        v-if="totalPages"
        v-slot:pageText="props"
      >
        Items {{ props.pageStart }} - {{ props.pageStop }} of {{ props.itemsLength }}
        <strong>{{ paginationText }}</strong>
      </template>
    </v-data-table>

    <!-- IDENTS DATA TABLE END -->

    <!-- ERROR DIALOG START -->
    <v-dialog
      v-model="showErrorDialog"
      max-width="390"
    >
      <v-card>
        <v-card-title class="error title white--text">
          <v-icon
            dark
            class="mr-2"
          >
            error_outline
          </v-icon>
          Error!
        </v-card-title>

        <v-card-text>
          {{ dialogMessage }}
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="secondary"
            flat
            @click="showErrorDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- ERROR DIALOG END -->

    <v-dialog
      v-model="approveIdentExpire"
      max-width="390"
    >
      <v-card>
        <v-card-title class="info title white--text">
          Info
        </v-card-title>

        <v-card-text>
          This action will set the document expiration date to past.
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            flat
            @click="requestUserIdentResultExpiration()"
          >
            ACCEPT
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import _ from 'lodash';
import { setQuery, downloadFile } from '@/helpers';

import { identStatus } from '@/enum/ident/IdentEnum';
import IdentDetailCard from '@/components/ident/IdentDetailCard';

export default {
  name: 'IdentsTable',
  components: {
    IdentDetailCard,
  },
  props: {
    currentUser: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      pagination: {},
      identStatus,
      headers: [
        { text: 'ID', value: 'identification_result.id' },
        { text: 'User', value: 'identification_result.user_id' },
        { text: 'Provider', value: 'identification_result.identification_provider_id' },
        { text: 'Status', value: 'identification_result.state' },
        { text: 'Documents', value: null },
        { text: 'Identification folder', value: 'identification_result.user_id' },
        { text: 'Created at', value: 'identification_result.created_at' },
        { text: 'Updated at', value: 'identification_result.updated_at' },
        { text: 'Actions', value: null, sortable: false },
      ],
      selectedIdentStatus: [],
      selectedProviders: [],
      search: '',
      error: null,
      showErrorDialog: false,
      approveIdentExpire: false,
      identExpireUserId: null,
      dialogMessage: '',
    };
  },
  computed: {
    idents() {
      return this.$store.state.identStore.idents;
    },
    totalIdents() {
      return this.$store.state.identStore.totalIdents;
    },
    totalPages() {
      return Math.ceil(this.totalIdents / this.pagination.rowsPerPage);
    },
    tableText() {
      if (this.currentUser) {
        return this.isLoading
          ? `Loading ${this.currentUser.first_name}  ${this.currentUser.last_name} ident cases`
          : `No ident cases found for ${this.currentUser.first_name}  ${this.currentUser.last_name}`;
      }

      return this.isLoading
        ? 'Loading ident cases'
        : 'No ident cases found.';
    },
    paginationText() {
      return `Page ${this.pagination.page} ${this.pagination.rowsPerPage === -1 ? '' : `of ${this.totalPages}`}`;
    },
    hasFilters() {
      return (
        this.selectedIdentStatus.length > 0 ||
        this.selectedProviders.length > 0 ||
        this.search.length > 0
      );
    },
    identStatuses() {
      return Object.keys(this.identStatus).map(item => this.identStatus[item]);
    },
    identProviders() {
      return this.$store.state.identStore.providers;
    },
  },

  watch: {
    pagination: {
      handler() {
        this.onFilterChange();
      },
      deep: true,
    },
    search: _.debounce(async function search(val) {
      this.onFilterChange(val);
    }, 600),

    $route() {
      this.getIdents();
    },
  },

  mounted() {
    this.syncURLParams();
  },

  methods: {
    syncURLParams() {
      // update pagination from query params and trigger route update to get idents
      const { query } = this.$route;

      this.pagination = {
        descending: (!(query.sortOrientation && query.sortOrientation === 'desc')),
        page: (parseInt(query.page, 0) || 1),
        rowsPerPage: (parseInt(query.itemsPerPage, 0) || 10),
        sortBy: (query.sort || 'identification_result.created_at'),
        totalItems: 0,
      };
    },
    getIdents() {
      this.isLoading = true;
      this.$store.dispatch('fetchIdents', this.$route.query)
        .finally(() => {
          this.isLoading = false;
        });
    },
    updateQuery(update) {
      setQuery({ ...this.$route.query, ...update }, this.$route, this.$router);
    },
    onFilterChange() {
      const query = {
        ...this.$route.query,
        sort: this.pagination.sortBy,
        sortOrientation: this.pagination.descending ? 'desc' : 'asc',
        page: this.pagination.page,
        itemsPerPage: this.pagination.rowsPerPage,
        search: this.getSearchParams(),
        providerName: this.selectedProviders.toString(),
        identState: this.selectedIdentStatus.toString(),
      };

      if (query.itemsPerPage === -1) {
        delete query.itemsPerPage;
      }

      this.updateQuery(query);
    },
    resetFilters() {
      this.selectedIdentStatus = [];
      this.selectedProviders = [];
      this.search = '';

      this.onFilterChange();
    },
    getUserFolder(id, type) {
      return `user/${id}/identfiles/${type}`;
    },
    getSearchParams() {
      const search = [];

      if (this.search.length > 0) {
        search.push(this.search);
      }

      return search.toString();
    },
    getUserIdentificationFiles(postData = { id: null, identType: null }) {
      if (!postData.id) {
        return null;
      }

      return this.$store.dispatch('fetchUserIdentificationFiles', postData)
        .then(async ({ data }) => {
          const filename = `user_${postData.id}_identification_files.zip`;
          downloadFile(data.uri, filename);
        })
        .catch(({ response }) => {
          this.dialogMessage = 'Error downloading user identification file';

          if (response) {
            this.dialogMessage = response.data.error;
          }

          this.showErrorDialog = true;
        });
    },
    openApproveIdentExpireDialog(userId) {
      this.identExpireUserId = userId;
      this.approveIdentExpire = true;
    },
    requestUserIdentResultExpiration() {
      this.isLoading = true;
      this.$store
        .dispatch('expireIdentResult', this.identExpireUserId)
        .then(() => {
          this.getIdents();
        })
        .catch((err) => {
          this.error = err;
        })
        .finally(() => {
          this.isLoading = false;
          this.approveIdentExpire = false;
          this.identExpireUserId = null;
        });
    },
  },
};
</script>
<style lang="scss">
.idents-table {
  .v-card__title {
    padding: 0 24px;
  }
  .filter {
    display: flex;
    flex: 1;
    align-items: center;
    flex-direction: row;
    justify-content: flex-start;
  }
  .filters {
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;
    padding: 16px 76px 0 24px;
    .v-select,
    .v-input {
      flex: 1;
      padding-right: 16px;
    }
  }
  .filters__header {
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;
    padding: 24px 24px 24px 0;
  }
  .v-datatable__actions__pagination {
    margin: 0 24px;
    strong {
      margin: 0 0 0 24px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ident/IdentsTable.vue